<!-- OnboardingOptionsImage.vue -->
<template>
  <div class="profile-selector" @dragover.prevent @drop.prevent="onFileDrop">
    <!-- 현재 선택된 프로필 -->
    <div
      class="profile-current"
      :class="
        inputImage.type === 'initial' ? `profile-color-${backgroundColor}` : ''
      "
    >
      <img v-if="inputImage.type === 'image'" :src="inputImage.src" />
      <h4 v-else class="accent initial">{{ nicknameInitial }}</h4>
    </div>

    <!-- 프로필 선택 UI는 onlyCurrentProfile이 아닐 때만 보임 -->
    <template v-if="!props.onlyCurrentProfile">
      <!-- 프로필 선택 -->
      <p class="font-dim accent">{{ t('onboarding_select_image') }}</p>
      <div class="wrapper-profile-option">
        <label class="profile-option" role="button">
          <input
            type="file"
            class="file-input-hidden"
            accept="image/*"
            @change="onFileSelect"
          />
          <h3 class="font-dimmer profile-ad">+</h3>
        </label>
        <div
          v-if="isVisibleInitial"
          :class="[
            'profile-option',
            'profile-color-navy',
            { active: inputImage.id === profileInitial.id },
          ]"
          @click="onClickImage(profileInitial)"
        >
          <h4 class="accent">{{ nicknameInitial }}</h4>
        </div>
        <div
          v-if="urlFilePreview"
          :class="[
            'profile-option',
            { active: inputImage.id === 'user-image' },
          ]"
          @click="onClickImage(null)"
        >
          <img :src="urlFilePreview" alt="Uploaded Image" />
          <a @click.prevent="removeFile"></a>
        </div>
        <div
          v-for="profile in listImageDefault"
          :key="profile.id"
          class="profile-option"
          :class="{ active: inputImage.id === profile.id }"
          @click="onClickImage(profile)"
        >
          <img :src="profile.src" />
        </div>
      </div>

      <!-- 배경색 선택도 onlyCurrentProfile이 아닐 때만 -->
      <div v-if="inputImage.id === 'initial-gi'" class="background-selector">
        <p class="dim accent">{{ t('onboarding_select_background_color') }}</p>
        <div class="wrapper-profile-option">
          <div
            v-for="color in listOptionBackgroundColor"
            :key="color"
            class="profile-option"
            :class="`profile-color-${color}`"
            @click="onClickBackgroundColor(color)"
          >
            <h4 class="accent">{{ nicknameInitial }}</h4>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import { useFileInput } from '@/composables/shared/useFileInput';
import { ProfileOption } from '@/types/components';

const { t } = useI18n();
const {
  urlFilePreview,
  listImageDefault,
  handleFileSelect,
  handleFileDrop,
  triggerFileInput,
  removeFile,
} = useFileInput();

const emit = defineEmits<{
  selectImage: [profileImage: ProfileOption];
  selectBackgroundColor: [color: string];
}>();

const props = withDefaults(
  defineProps<{
    inputImage: ProfileOption;
    onlyCurrentProfile?: boolean;
    backgroundColor: string;
    nicknameInitial: string;
    isVisibleInitial?: boolean; // 기본 프로필(이니셜) 옵션이 보이는지
  }>(),
  {
    onlyCurrentProfile: false,
    backgroundColor: 'navy',
    nicknameInitial: '',
    isVisibleInitial: true,
  }
);

// 기본 프로필 (이니셜)
const profileInitial: ProfileOption = {
  id: 'initial-gi',
  label: props.nicknameInitial,
  type: 'initial',
};

const listOptionBackgroundColor = ['navy', 'yellow', 'pink', 'purple', 'green'];

// 프로필 사진 선택 시
const onClickImage = (profile: ProfileOption | null) => {
  if (!profile)
    profile = {
      id: 'user-image',
      label: 'user-image',
      type: 'image',
      src: urlFilePreview.value,
    };
  emit('selectImage', profile);
};

// 프로필 배경색 선택 시
const onClickBackgroundColor = (color: string) => {
  emit('selectBackgroundColor', color);
};

// 파일 선택 시
const onFileSelect = (event) => {
  handleFileSelect(event);
  onClickImage(null);
};

// 파일 드래그앤드롭 발생 시
const onFileDrop = (event) => {
  handleFileDrop(event);
  onClickImage(null);
};
</script>

<style scoped lang="scss">
@import '@/assets/sass/theme/_light.scss';

.profile-selector {
  display: flex;
  flex-direction: column;
  margin: 0.5rem 0;

  .profile-current {
    width: 6rem;
    height: 6rem;
    border-radius: 50%;
    align-self: center;
    overflow: hidden;
    margin-bottom: 1rem;
    background: $color-gray-10;
    border: 1px solid $border-default;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .initial {
      width: 100%;
      height: 100%;
      text-align: center;
      line-height: 6rem;
    }
  }

  .wrapper-profile-option {
    display: flex;
    gap: 0.5rem;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 0.25rem;
    margin-top: 0.5rem;

    .profile-option {
      flex: 0 0 auto;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: $surface-default;
      cursor: pointer;

      &.active {
        border: 4px solid $border-default;
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }
    }

    .profile-add {
      width: 2.5rem;
      height: 2.5rem;
      line-height: 2.25rem;
      text-align: center;
      border-radius: 50%;
      border: 1px dashed $border-dark;
    }
  }

  .background-selector {
    margin-top: 1rem;
  }
}

.profile-color-navy {
  background-color: $profile-navy-bg !important;
  color: $profile-navy-font;
}

.profile-color-yellow {
  background-color: $profile-yellow-bg !important;
  color: $profile-yellow-font;
}

.profile-color-pink {
  background-color: $profile-pink-bg !important;
  color: $profile-pink-font;
}

.profile-color-purple {
  background-color: $profile-purple-bg !important;
  color: $profile-purple-font;
}

.profile-color-green {
  background-color: $profile-green-bg !important;
  color: $profile-green-font;
}

.file-input-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
</style>
